"use client";

import styled, { type CSSProperties } from "styled-components";
import Chevron from "@/assets/icons/Chevron.svg";
import { useEffect, useState } from "react";
import Image from "next/image";
import theme from "@/themes/velys-theme";
import LocaleIcon from "@/assets/icons/LocaleIcon.svg";

export type IDropdownOption = {
  value?: string | number | null;
  name: string;
};

interface DropdownProps {
  styles?: CSSProperties;
  options?: IDropdownOption[];
  selectedOption: string;
  disabled?: boolean;
  onSelect: (e: React.MouseEvent<HTMLLIElement>) => void;
  color: string;
  backgroundColor: string;
}

interface IDropdown {
  $disabled?: boolean;
  $error?: boolean;
  $isOpened: boolean;
  $isView?: boolean;
  $color: string;
  $backgroundColor: string;
}

const StyledDropdown = styled.div<IDropdown>`
  display: flex;
  flex-direction: column;
  height: fit-content;
  // max-height: 40px;
  max-width: fit-content;
  width: 100%;
  position: relative;
  flex: 1;

  .row {
    background-color: #fff;
    border: none;
    border-left: 3px solid
      ${(props) => (props.$disabled ? props.theme.lightGrey : props.$color)};
    border-radius: 0;
    position: relative;
    cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    padding: 1px 15px;
    padding-bottom: 4px;
    gap: 5px;
    margin-bottom: 4px;
  }

  .option {
    font-size: 1rem;
    color: ${(props) => (props.$disabled ? "#d5d5d5" : "#141414")};
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    margin-right: 10px;
  }

  #opened {
    transform: rotate(180deg);
  }

  .options {
    display: flex;
    flex-direction: column;
    border: none;
    border-radius: 5px;
    background-color: #fff;

    position: absolute;
    top: 30px;
    z-index: 5;
    right: 5px;
    width: 90%;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 1024px) {
    /* Add full border back on mobile */
    .row {
      padding: 5px;
      border: 1px solid
        ${(props) => (props.$disabled ? props.theme.lightGrey : props.$color)};
      border-left: 1px solid
        ${(props) => (props.$disabled ? props.theme.lightGrey : props.$color)};
      border-radius: 5px;
      margin-bottom: 0;
    }

    .globe {
      height: 15px;
      width: 15px;
      opacity: ${(props) => props.$disabled && "50%"};
    }

    .arrow {
      height: 13px;
      width: 13px;
      opacity: ${(props) => props.$disabled && "50%"};
    }

    .option {
      font-size: 0.85rem;
      position: relative;
      top: auto;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      margin-right: 5px;
    }

    .options {
      top: 26px;
      left: 0px;
      border: 1px solid ${(props) => props.$color};
      border-radius: 0 0 3px 3px;
      box-shadow: none;

      right: 0px;
      width: 100.6%;
    }
  }
`;

const StyledUl = styled.ul`
  font-size: 1rem;
  height-fit-content;
  overflow-y: auto;
  list-style-type: none;
  padding: 0;
  margin: 0;

  li {
    cursor: pointer;
    padding: 8px 10px;
    font-weight: 500;
    color: ${(props) => props.theme.black};

    &:hover {
      color: ${(props) => props.theme.main};
    }
  }

  @media (max-width: 1024px) {
    font-size: 0.85rem;
  }
`;

export function LocaleDropdown({
  styles,
  selectedOption,
  options,
  disabled = false,
  onSelect,
  color,
  backgroundColor,
}: DropdownProps) {
  const [selected, setSelectedOption] = useState(selectedOption);
  const [isToggled, setIsToggled] = useState(false);

  useEffect(() => {
    if (selectedOption) {
      const findName = options?.find(
        (option) => option.value === selectedOption.toString()
      );
      if (findName) {
        setSelectedOption(findName.name);
      } else {
        setSelectedOption(selectedOption);
      }
    }
  }, [selectedOption, options]);

  return (
    <StyledDropdown
      $disabled={disabled}
      $isOpened={isToggled}
      $color={color}
      $backgroundColor={backgroundColor}
      style={styles}
    >
      <div className="row" onClick={() => setIsToggled(!isToggled)}>
        <Image
          src={LocaleIcon.src}
          alt="globe-icon"
          className="globe"
          width={20}
          height={20}
        />
        <div className="option">{selected}</div>
        <Image
          src={Chevron.src}
          className="arrow"
          alt="chevron"
          id={isToggled ? "opened" : ""}
          width={18}
          height={18}
        />
        {isToggled && (
          <div className="options">
            <StyledUl>
              {options!.map((option: IDropdownOption) => (
                <li
                  title={option.value?.toString() || option.name}
                  role="presentation"
                  data-value={option.value || option.name || option}
                  key={`opt-${option.value || option.name || option}`}
                  onClick={(e) => {
                    onSelect(e);
                    setIsToggled(!isToggled);
                    setSelectedOption(option.value?.toString() || option.name);
                  }}
                  style={{
                    color:
                      option.value?.toString() === selectedOption
                        ? theme.main
                        : undefined,
                    pointerEvents:
                      option.value?.toString() === selectedOption
                        ? "none"
                        : "auto",
                  }}
                >
                  {option.name}
                </li>
              ))}
            </StyledUl>
          </div>
        )}
      </div>
    </StyledDropdown>
  );
}
